import React, { useEffect, useState } from "react";
import { Page, PageHeader } from "../components/PageHeader";
import { api, extractError } from "../lib/api";
import { fmtCLP, todayISO } from "../lib/format";
import { toast } from "sonner";
import { Calculator, FilePlus2 } from "lucide-react";
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogDescription, DialogTrigger,
} from "../components/ui/dialog";
import { LibroPagination, useClientPagination } from "../components/LibroPagination";

export default function Provisiones() {
  const [vac, setVac] = useState(null);
  const [renta, setRenta] = useState(null);
  const [hist, setHist] = useState([]);
  const [loading, setLoading] = useState(false);
  const [openManual, setOpenManual] = useState(false);
  const [fecha, setFecha] = useState(todayISO());
  const [manual, setManual] = useState({
    concepto: "", monto: "", cuenta_gasto_codigo: "5.2.04", cuenta_provision_codigo: "2.1.03", glosa: "",
  });

  const load = async () => {
    setLoading(true);
    try {
      const [v, r, h] = await Promise.all([
        api.get("/provisiones/vacaciones"),
        api.get("/provisiones/renta"),
        api.get("/provisiones/historial"),
      ]);
      setVac(v.data);
      setRenta(r.data);
      setHist(h.data);
    } catch (e) {
      toast.error(extractError(e));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);
  const histPage = useClientPagination(hist, `${hist.length}`);

  const contabilizar = async (tipo) => {
    const label = tipo === "vacaciones" ? "vacaciones" : "impuesto a la renta";
    if (!window.confirm(`¿Generar asiento de provisión de ${label} (borrador)?`)) return;
    try {
      const { data } = await api.post(`/provisiones/${tipo}/contabilizar`, {
        fecha,
        solo_ajuste: true,
      });
      toast.success(`Provisión ${label}: ${fmtCLP(data.monto)} · borrador en Libro diario`);
      load();
    } catch (e) {
      toast.error(extractError(e));
    }
  };

  const submitManual = async (e) => {
    e.preventDefault();
    try {
      await api.post("/provisiones/manual", {
        fecha,
        concepto: manual.concepto,
        monto: parseFloat(manual.monto) || 0,
        cuenta_gasto_codigo: manual.cuenta_gasto_codigo,
        cuenta_provision_codigo: manual.cuenta_provision_codigo,
        glosa: manual.glosa,
      });
      toast.success("Provisión manual en borrador");
      setOpenManual(false);
      setManual({ concepto: "", monto: "", cuenta_gasto_codigo: "5.2.04", cuenta_provision_codigo: "2.1.03", glosa: "" });
      load();
    } catch (err) {
      toast.error(extractError(err));
    }
  };

  return (
    <Page>
      <PageHeader
        title="Provisiones"
        subtitle="Vacaciones, impuesto a la renta (estimado) y cargos manuales → asientos en borrador"
        testid="prov-header"
        actions={
          <div className="flex items-center gap-2 flex-wrap">
            <label className="text-sm flex items-center gap-1.5">
              Fecha asiento
              <input type="date" value={fecha} onChange={(e) => setFecha(e.target.value)}
                className="border border-border rounded-sm px-2 py-1 text-sm" />
            </label>
            <Dialog open={openManual} onOpenChange={setOpenManual}>
              <DialogTrigger asChild>
                <button className="inline-flex items-center gap-1.5 border border-border hover:bg-muted text-sm px-3 py-1.5 rounded-sm">
                  <FilePlus2 className="h-4 w-4" /> Manual
                </button>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader>
                  <DialogTitle>Provisión manual</DialogTitle>
                  <DialogDescription>Debe gasto / Haber pasivo de provisión.</DialogDescription>
                </DialogHeader>
                <form onSubmit={submitManual} className="space-y-3">
                  <label className="block text-sm">Concepto
                    <input required value={manual.concepto} onChange={(e) => setManual({ ...manual, concepto: e.target.value })}
                      className="mt-1 w-full border border-border rounded-sm px-2 py-1.5 text-sm" />
                  </label>
                  <label className="block text-sm">Monto
                    <input type="number" required min="1" value={manual.monto}
                      onChange={(e) => setManual({ ...manual, monto: e.target.value })}
                      className="mt-1 w-full border border-border rounded-sm px-2 py-1.5 text-sm" />
                  </label>
                  <div className="grid grid-cols-2 gap-3">
                    <label className="block text-sm">Cuenta gasto
                      <input value={manual.cuenta_gasto_codigo}
                        onChange={(e) => setManual({ ...manual, cuenta_gasto_codigo: e.target.value })}
                        className="mt-1 w-full border border-border rounded-sm px-2 py-1.5 text-sm" />
                    </label>
                    <label className="block text-sm">Cuenta provisión
                      <input value={manual.cuenta_provision_codigo}
                        onChange={(e) => setManual({ ...manual, cuenta_provision_codigo: e.target.value })}
                        className="mt-1 w-full border border-border rounded-sm px-2 py-1.5 text-sm" />
                    </label>
                  </div>
                  <DialogFooter>
                    <button type="submit" className="bg-primary text-primary-foreground text-sm px-3 py-1.5 rounded-sm">Generar</button>
                  </DialogFooter>
                </form>
              </DialogContent>
            </Dialog>
          </div>
        }
      />

      {loading ? (
        <div className="p-8 text-center text-muted-foreground text-sm">Cargando…</div>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mb-5">
          <Card title="Provisión de vacaciones" testid="prov-vac">
            {vac ? (
              <>
                <Fila label="Trabajadores con días" value={vac.trabajadores} />
                <Fila label="Provisión objetivo" value={fmtCLP(vac.total)} fuerte />
                <Fila label="Saldo cuenta 2.1.14" value={fmtCLP(vac.saldo_cuenta_actual)} />
                <Fila label="Ajuste sugerido" value={fmtCLP(vac.ajuste_sugerido)} tono={vac.ajuste_sugerido > 0 ? "text-primary" : undefined} />
                <p className="text-[11px] text-muted-foreground mt-2">{vac.aviso}</p>
                <button
                  onClick={() => contabilizar("vacaciones")}
                  disabled={!vac.ajuste_sugerido}
                  data-testid="prov-vac-btn"
                  className="mt-3 inline-flex items-center gap-1.5 bg-primary text-primary-foreground disabled:opacity-40 text-sm px-3 py-1.5 rounded-sm"
                >
                  <Calculator className="h-4 w-4" /> Contabilizar ajuste
                </button>
                {vac.detalle?.length > 0 && (
                  <div className="mt-4 max-h-48 overflow-auto border border-border rounded-sm">
                    <table className="acc-table text-[12px]">
                      <thead><tr><th>Trabajador</th><th className="num">Días</th><th className="num">Monto</th></tr></thead>
                      <tbody>
                        {vac.detalle.slice(0, 30).map((d) => (
                          <tr key={d.trabajador_id}>
                            <td>{d.nombre || d.rut}</td>
                            <td className="num">{d.dias_pendientes}</td>
                            <td className="num">{fmtCLP(d.monto)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </>
            ) : <p className="text-sm text-muted-foreground">Sin datos</p>}
          </Card>

          <Card title="Provisión impuesto a la renta" testid="prov-renta">
            {renta ? (
              <>
                <Fila label={`AT / año ${renta.anio}`} value={`${renta.meses_transcurridos} meses`} />
                <Fila label="Utilidad anualizada" value={fmtCLP(renta.utilidad_anualizada)} />
                <Fila label={`Impuesto est. (${renta.tasa_pct} %)`} value={fmtCLP(renta.impuesto_estimado)} />
                <Fila label="PPM reconocidos (aprox.)" value={`− ${fmtCLP(renta.ppm_reconocidos)}`} />
                <Fila label="Provisión objetivo" value={fmtCLP(renta.monto_provision)} fuerte />
                <Fila label="Saldo cuenta 2.1.13" value={fmtCLP(renta.saldo_cuenta_actual)} />
                <Fila label="Ajuste sugerido" value={fmtCLP(renta.ajuste_sugerido)} tono={renta.ajuste_sugerido > 0 ? "text-primary" : undefined} />
                <p className="text-[11px] text-muted-foreground mt-2">{renta.aviso}</p>
                <button
                  onClick={() => contabilizar("renta")}
                  disabled={!renta.ajuste_sugerido}
                  data-testid="prov-renta-btn"
                  className="mt-3 inline-flex items-center gap-1.5 bg-primary text-primary-foreground disabled:opacity-40 text-sm px-3 py-1.5 rounded-sm"
                >
                  <Calculator className="h-4 w-4" /> Contabilizar ajuste
                </button>
              </>
            ) : <p className="text-sm text-muted-foreground">Sin datos</p>}
          </Card>
        </div>
      )}

      <div className="bg-card border border-border rounded-md overflow-x-auto">
        <div className="px-4 py-2 border-b border-border label-caps">Historial</div>
        <table className="acc-table">
          <thead>
            <tr><th>Fecha</th><th>Tipo</th><th className="num">Monto</th><th>Asiento</th></tr>
          </thead>
          <tbody>
            {histPage.pageItems.length === 0 ? (
              <tr><td colSpan={4} className="text-center text-muted-foreground text-sm py-6">Sin provisiones registradas</td></tr>
            ) : histPage.pageItems.map((h) => (
              <tr key={h.id}>
                <td>{h.fecha}</td>
                <td className="text-[11px] uppercase">{h.tipo}{h.concepto ? ` · ${h.concepto}` : ""}</td>
                <td className="num font-semibold">{fmtCLP(h.monto)}</td>
                <td className="text-[11px] text-muted-foreground font-mono">{h.asiento_id?.slice(0, 8)}…</td>
              </tr>
            ))}
          </tbody>
        </table>
        <LibroPagination pagination={histPage.pagination} pageSize={histPage.pageSize} onPageSizeChange={histPage.setPageSize} onPageChange={histPage.setPage} testidPrefix="prov-hist" />
      </div>
    </Page>
  );
}

function Card({ title, children, testid }) {
  return (
    <div className="bg-card border border-border rounded-md p-5" data-testid={testid}>
      <h3 className="font-display text-xl mb-3">{title}</h3>
      {children}
    </div>
  );
}

function Fila({ label, value, fuerte, tono }) {
  return (
    <div className={`flex justify-between gap-3 py-1.5 text-sm ${fuerte ? "font-semibold border-t border-border mt-1 pt-2.5" : "border-b border-border/60"}`}>
      <span className="text-muted-foreground">{label}</span>
      <span className={`num ${tono || ""}`}>{value}</span>
    </div>
  );
}
